import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';
import api from '../API/Api';

const templateurl = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4';

const raritycolor = {
  common:    '#9e9e9e',
  uncommon:  '#52c078',
  rare:      '#5a9ee0',
  elite:     '#a070e0',
  epic:      '#c060d0',
  legendary: '#f0ab20',
  boss:      '#e84c3d',
  unique:    '#00e5c8',
};

function SearchResults({ query, onClear, items, mobs }) {
  const q = query.toLowerCase().trim();
  const itemResults = items.filter(i =>
    i.name.toLowerCase().includes(q) ||
    i.type?.toLowerCase().includes(q) ||
    i.category?.toLowerCase().includes(q)
  );
  const mobResults = mobs.filter(m =>
    m.name.toLowerCase().includes(q) ||
    m.type?.toLowerCase().includes(q)
  );
  const total = itemResults.length + mobResults.length;

  return (
    <div className="wh-search-results">
      <div className="wh-search-header">
        <span className="wh-search-count">{total} result{total !== 1 ? 's' : ''} for "{query}"</span>
        <button className="wh-search-clear" onClick={onClear}>Clear</button>
      </div>

      {total === 0 && (
        <div className="wh-search-empty">No items or mobs match that search.</div>
      )}

      {itemResults.length > 0 && (
        <div className="wh-search-section">
          <div className="wh-section-head">
            <span className="wh-section-head-text">Items ({itemResults.length})</span>
          </div>
          <StaggerContainer className="wh-result-list">
            {itemResults.map(item => (
              <StaggerItem key={item.id}>
                <Link to={`/wiki/item/${item.slug}`} className="wh-result-row">
                  <div className="wh-result-info">
                    <span className="wh-result-name">{item.name}</span>
                    <span className="wh-result-meta">{item.category} · {item.type}</span>
                  </div>
                  <span
                    className="wh-result-rarity"
                    style={{ color: raritycolor[item.rarity] || '#9e9e9e' }}
                  >
                    {item.rarity}
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      )}

      {mobResults.length > 0 && (
        <div className="wh-search-section">
          <div className="wh-section-head">
            <span className="wh-section-head-text">Mobs ({mobResults.length})</span>
          </div>
          <StaggerContainer className="wh-result-list">
            {mobResults.map(mob => (
              <StaggerItem key={mob.id}>
                <Link to={`/wiki/mob/${mob.slug}`} className="wh-result-row">
                  <div className="wh-result-info">
                    <span className="wh-result-name">{mob.name}</span>
                    <span className="wh-result-meta">{mob.type}</span>
                  </div>
                  <span
                    className="wh-result-rarity"
                    style={{ color: raritycolor[mob.tier] || '#9e9e9e' }}
                  >
                    {mob.tier}
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      )}
    </div>
  );
}

const IMPACT_COLOR = { minor: '#9e9e9e', major: '#f0ab20', rework: '#e84c3d' };
const TAG_COLOR = {
  balance: '#f0ab20', bugfix: '#e84c3d', new_content: '#52c078',
  mechanic: '#5a9ee0', rework: '#a070e0', removed: '#9e9e9e', custom: '#ccc',
};
const TAG_LABEL = {
  balance: 'Balance', bugfix: 'Bugfix', new_content: 'New Content',
  mechanic: 'Mechanic', rework: 'Rework', removed: 'Removed', custom: 'Note',
};

function WhatsNew({ version }) {
  if (!version) return null;
  const impactC  = IMPACT_COLOR[version.impact] || '#9e9e9e';
  const entries  = version.entries || [];
  const tagCounts = entries.reduce((acc, e) => { acc[e.tag] = (acc[e.tag] || 0) + 1; return acc; }, {});

  return (
    <FadeIn y={10} duration={0.6}>
      <div style={{
        margin: '1.5rem 0',
        border: `1px solid ${impactC}44`,
        borderLeft: `3px solid ${impactC}`,
        borderRadius: 10,
        background: `${impactC}0a`,
        padding: '0.9rem 1.1rem',
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
      }}>
        <McIcon materialKey="book" size={22} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
            <span style={{
              background: `${impactC}22`, color: impactC, border: `1px solid ${impactC}44`,
              borderRadius: 4, padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em',
            }}>
              {(version.impact || 'minor').toUpperCase()}
            </span>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)' }}>
              {version.version} · {version.title}
            </span>
          </div>
          {Object.keys(tagCounts).length > 0 && (
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {Object.entries(tagCounts).map(([tag, n]) => {
                const c = TAG_COLOR[tag] || '#ccc';
                return (
                  <span key={tag} style={{
                    background: `${c}18`, color: c, border: `1px solid ${c}33`,
                    borderRadius: 10, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 600,
                  }}>
                    {n} {TAG_LABEL[tag] || tag}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <Link
          to={`/wiki/updates/${version.id}`}
          style={{
            color: 'var(--teal)', textDecoration: 'none', fontSize: '0.82rem',
            fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          What's New →
        </Link>
      </div>
    </FadeIn>
  );
}

/* 3D tilt handlers shared by category cards */
function tiltMove(e) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const px = (e.clientX - r.left) / r.width - 0.5;
  const py = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty('--ry', `${(px * 8).toFixed(2)}deg`);
  el.style.setProperty('--rx', `${(-py * 6).toFixed(2)}deg`);
  el.style.setProperty('--mx', `${e.clientX - r.left}px`);
  el.style.setProperty('--my', `${e.clientY - r.top}px`);
}

function tiltReset(e) {
  const el = e.currentTarget;
  el.style.setProperty('--rx', '0deg');
  el.style.setProperty('--ry', '0deg');
}

function CategoryCard({ cat, large }) {
  return (
    <Link
      to={cat.link}
      className={`wh2-cat${large ? ' wh2-cat--large' : ''}`}
      style={{ '--cc': cat.color }}
      onMouseMove={tiltMove}
      onMouseLeave={tiltReset}
    >
      <div className="wh2-cat-spotlight" aria-hidden="true" />
      <div className="wh2-cat-bigicon" aria-hidden="true">
        <McIcon materialKey={cat.material} size={large ? 150 : 110} />
      </div>

      <div className="wh2-cat-orb">
        <McIcon materialKey={cat.material} size={large ? 40 : 30} />
      </div>

      <div className="wh2-cat-text">
        <span className="wh2-cat-title">{cat.title}</span>
        {cat.total != null && <span className="wh2-cat-count">{cat.total} entries</span>}
        <span className="wh2-cat-desc">{cat.desc}</span>

        {cat.previews?.length > 0 && (
          <div className="wh-preview-chips">
            {cat.previews.map(name => (
              <span key={name} className="wh-preview-chip">{name}</span>
            ))}
            {cat.total > cat.previews.length && (
              <span className="wh-preview-more">+{cat.total - cat.previews.length} more</span>
            )}
          </div>
        )}
      </div>

      <span className="wh2-cat-arrow">Browse →</span>
    </Link>
  );
}

export default function WikiHome({ items = [], mobs = [], roots = [] }) {
  const [latestVersion, setLatestVersion] = useState(null);

  useEffect(() => {
    api('getwikiversionsv2').then(res => {
      if (!res.isSuccess) return;
      const published = (res.data || []).filter(v => v.status === 'published');
      if (published.length) setLatestVersion(published[0]);
    });
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const clearSearch = () => setSearchParams({});

  const enabledItems = items.filter(i => i.enabled !== 0 && i.enabled !== '0');
  const enabledMobs  = mobs.filter(m => m.enabled !== 0 && m.enabled !== '0');
  const enabledRoots = roots.filter(r => Number(r.enabled) !== 0 && r.status !== 'stub');

  const mainCategories = [
    {
      title: 'The Roots',
      desc: 'Every elemental power: abilities, passives, ultimates and how to unlock rarer Roots.',
      color: '#8a4dff',
      material: 'nether_star',
      link: '/wiki/roots',
      total: enabledRoots.length || null,
      previews: enabledRoots.slice(0, 4).map(r => r.name),
    },
    {
      title: 'Custom Items',
      desc: 'All custom weapons, tools and armour: stats, crafting recipes and special abilities.',
      color: '#e84c3d',
      material: 'netherite_sword',
      link: '/wiki/items',
      total: enabledItems.length || null,
      previews: enabledItems.slice(0, 4).map(i => i.name),
    },
    {
      title: 'Custom Mobs',
      desc: 'Unique creatures with custom drops. Spawn conditions, drop tables and combat tips.',
      color: '#a070e0',
      material: 'creeper_head',
      link: '/wiki/mobs',
      total: enabledMobs.length || null,
      previews: enabledMobs.slice(0, 4).map(m => m.name),
    },
  ];

  const subCategories = [
    {
      title: 'Supply Drops',
      desc: 'Where crates spawn, rarity odds and every possible loot drop.',
      color: '#c8b800',
      material: 'chest',
      link: '/wiki/supply-drops',
    },
    {
      title: 'Meteors',
      desc: 'Scheduled meteor strikes, crater loot and how to contest them.',
      color: '#e8642c',
      material: 'fire_charge',
      link: '/wiki/meteors',
    },
    {
      title: 'Patch Notes',
      desc: 'Every version, every buff, nerf and rework since launch.',
      color: '#5a9ee0',
      material: 'book',
      link: '/wiki/updates',
    },
    {
      title: 'Season Info',
      desc: 'Current season details, launch info and live server stats.',
      color: '#52c078',
      material: 'clock',
      link: '/wiki/season',
    },
  ];

  const heroStats = [
    { label: 'Roots', value: enabledRoots.length, color: '#8a4dff' },
    { label: 'Custom Items', value: enabledItems.length, color: '#e84c3d' },
    { label: 'Custom Mobs', value: enabledMobs.length, color: '#a070e0' },
  ].filter(s => s.value > 0);

  return (
    <>
      <div className="wh-video-hero wh2-hero">
        <video className="wh-video-hero-vid" src={templateurl} autoPlay loop muted playsInline />
        <div className="wh-video-hero-overlay" />
        <FadeIn className="wh-video-hero-content" y={30} duration={0.8}>
          <div className="wh2-eyebrow">Community Wiki</div>
          <h1 className="wh-title">Simple<em>MC</em> Wiki</h1>
          <p className="wh-subtitle">
            Everything you need to know about SimpleMC, in one place
          </p>
          {heroStats.length > 0 && (
            <div className="wh2-hero-stats">
              {heroStats.map(s => (
                <div key={s.label} className="wh2-hero-stat" style={{ '--cc': s.color }}>
                  <span className="wh2-hero-stat-val">{s.value}</span>
                  <span className="wh2-hero-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          )}
        </FadeIn>
      </div>

      <div className="wh2-container">
        {latestVersion && <WhatsNew version={latestVersion} />}

        {query ? (
          <div style={{ padding: '1.5rem 0 3rem' }}>
            <SearchResults query={query} onClear={clearSearch} items={items} mobs={mobs} />
          </div>
        ) : (
          <>
            <div className="wh2-sec-head">
              <span className="wh2-eyebrow wh2-eyebrow--center">Browse the wiki</span>
              <h2 className="wh2-sec-title">Where do you want to <em>start?</em></h2>
            </div>

            <StaggerContainer className="wh2-cats-main">
              {mainCategories.map(cat => (
                <StaggerItem key={cat.title} style={{ display: 'flex' }}>
                  <CategoryCard cat={cat} large />
                </StaggerItem>
              ))}
            </StaggerContainer>

            <StaggerContainer className="wh2-cats-sub" style={{ marginBottom: '3rem' }}>
              {subCategories.map(cat => (
                <StaggerItem key={cat.title} style={{ display: 'flex' }}>
                  <CategoryCard cat={cat} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </>
        )}
      </div>
    </>
  );
}
